import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getPreferences, hasSavedPreferences, savePreferences } from "@/lib/preferences";

const AuthContext = createContext(null);

const syncPreferences = async () => {
  try {
    const { data } = await api.get("/settings/general");
    const current = getPreferences();
    savePreferences({
      timezone: data.timezone,
      date_format: data.date_format,
      time_format: data.time_format,
      theme: hasSavedPreferences() ? current.theme : data.default_theme,
    });
  } catch {}
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await syncPreferences();
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("token", data.token);
      setUser(data.user);
      await syncPreferences();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    setUser(false);
  }, []);

  const role = user?.role;
  const isAdmin = role === "admin";
  const isOpMayor = role === "operator_mayor";
  const isOpDetalle = role === "operator_detalle";
  const isReadOnly = role === "readonly";
  const isOperator = isOpMayor || isOpDetalle;
  const canWrite = isAdmin || isOperator;
  const canSeeFinancial = isAdmin || isReadOnly;
  const canSeeMarket = (m) => {
    if (isAdmin || isReadOnly) return true;
    if (isOpMayor) return m === "mayor";
    if (isOpDetalle) return m === "detalle";
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, role, isAdmin, isOpMayor, isOpDetalle, isOperator, isReadOnly, canWrite, canSeeFinancial, canSeeMarket }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
